import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  userAddModalRecoil,
  userModifyModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import UserAddModal from '@/modal/UserAddModal';
import UserModifyModal from '@/modal/UserModifyModal';
import { hangjungdong } from '@/data/hangjungdong';
import { UserApi } from '@/sdk/UserApi';
import {
  UserControllerGetUsersParams,
  ChangeUserStateDtoTx,
  Users,
} from '@/sdk/data-contracts';
import {
  userListRecoil,
  userTypesRecoil,
  userSitesRecoil,
} from '@/atom/UserAtom';

const Userlist = () => {
  const userApi = new UserApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [userAddModalState, setUserAddModalState] =
    useRecoilState(userAddModalRecoil);
  const [userModifyModalState, setUserModifyModalState] = useRecoilState(
    userModifyModalRecoil,
  );
  const [userListState, setUserListState] = useRecoilState(userListRecoil);
  const [userTypesState, setUserTypesState] = useRecoilState(userTypesRecoil);
  const [userSitesState, setUserSitesState] = useRecoilState(userSitesRecoil);

  const [userType, setUserType] = useState<string>('');
  const [sido, setSido] = useState<string>('');
  const [sigungu, setSigungu] = useState<string>('');
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [checkItems, setCheckItems] = useState<number[]>([]);
  const [listLength, setListLength] = useState<number>(0);
  const [modifyNum, setModifyNum] = useState<number>(0);

  const allCheckBtnRef = useRef<HTMLInputElement>(null);

  const downloadCSV = async (saveFileName: string) => {
    let allList: Users[] | undefined;

    const dto: UserControllerGetUsersParams = {
      sortBy,
      order,
    };

    try {
      const { data } = await userApi.userControllerGetUsers(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.users) allList = data.users;
    } catch (err: any) {}

    if (!allList || allList.length === 0) {
      setErrorModalText('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = [
      'No',
      '아이디',
      '회원 구분',
      '사이트',
      '지역 구분',
      '이름',
      '휴대전화',
      '이메일',
      '등록일',
      '승인상태',
    ];

    const rows = allList.map((list, index) => [
      index + 1,
      list.passid,
      list.userType,
      list.site,
      list.regionClassification,
      list.name,
      list.phone,
      list.email,
      list.createdAt,
      list.state,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${saveFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filterUserList = async () => {
    const transformSido =
      sido === '전국'
        ? '전국'
        : hangjungdong.sido.find((list) => list.sido === sido)?.codeNm || '';
    const transformSigungu = hangjungdong.sigungu.find(
      (list) => list.sido === sido && list.sigungu === sigungu,
    )?.codeNm;

    const dto: UserControllerGetUsersParams = {
      userType,
      sido: transformSido ? transformSido : '',
      sigungu: transformSigungu ? transformSigungu : '',
      ...(searchCondition && { searchCondition }),
      searchKeyword,
      offset,
      limit,
      sortBy,
      order,
    };

    try {
      const { data } = await userApi.userControllerGetUsers(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.users) setUserListState(data.users);
      if (data.totalColumn) {
        setListLength(data.totalColumn);
      } else if (data.totalColumn === 0) {
        setListLength(0);
      }
    } catch (err: any) {}
  };

  const fetchUserTypesAndSites = async () => {
    try {
      const { data } = await userApi.userControllerGetSignUpUser();
      setUserTypesState(data.userTypes);
      setUserSitesState(data.sites);
    } catch (err: any) {}
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckItems([]);
    setOffset(0);
    filterUserList();
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
    setCheckItems([]);
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleSingleCheck = (checked: boolean, id: number) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked) {
      const idArray: number[] = [];
      userListState?.forEach((el) => idArray.push(Number(el.id)));
      setCheckItems(idArray);
    } else {
      setCheckItems([]);
    }
  };

  const handleSelectApproval = async () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }

    try {
      const dto: ChangeUserStateDtoTx = {
        ids: checkItems,
      };
      await userApi.userControllerApproveUser(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  const handleSelectCompanion = async () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }

    try {
      const dto: ChangeUserStateDtoTx = {
        ids: checkItems,
      };
      await userApi.userControllerRejectUser(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    filterUserList();
    fetchUserTypesAndSites();
  }, [limit, offset]);

  return (
    <>
      <CntBox1>
        <TitleTextBox text="회원 목록" />
        <form onSubmit={handleSearch}>
          <SearchOptionBox>
            <InputWrap>
              <InputArea>
                <IS.SelectType1>
                  <select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">회원 구분</option>
                    {userTypesState.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.SelectType1>
                  <select
                    value={sido}
                    onChange={(e) => {
                      setSido(e.target.value);
                      setSigungu('');
                    }}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">-시/도-</option>
                    <option value="전국">전국</option>
                    {hangjungdong.sido.map((list) => (
                      <option key={list.sido} value={list.sido}>
                        {list.codeNm}
                      </option>
                    ))}
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.SelectType1>
                  <select
                    value={sigungu}
                    onChange={(e) => {
                      setSigungu(e.target.value);
                    }}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">-시군구-</option>
                    {sido &&
                      hangjungdong.sigungu
                        .filter((list) => list.sido === sido)
                        .map((list) => (
                          <option key={list.sigungu} value={list.sigungu}>
                            {list.codeNm}
                          </option>
                        ))}
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.SelectType1>
                  <select
                    value={searchCondition}
                    onChange={(e) => {
                      setSearchCondition(e.target.value);
                      setSearchKeyword('');
                    }}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">검색 조건</option>
                    <option value="아이디">아이디</option>
                    <option value="사이트명">사이트명</option>
                    <option value="이름">이름</option>
                    <option value="휴대전화">휴대전화</option>
                    <option value="이메일">이메일</option>
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.TextInputType1
                  style={{ width: 320 }}
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  disabled={searchCondition === '' ? true : false}
                  placeholder="검색어 입력"
                />
              </InputArea>
            </InputWrap>
            <SearchBtn />
          </SearchOptionBox>
        </form>
        <TableOpitonBox>
          <h1>
            전체 <span>{listLength.toLocaleString()}</span>
          </h1>
          <div className="right-box">
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type5
              type="button"
              onClick={() => downloadCSV('user_list')}
            >
              Excel 다운로드
              <span className="img-box">
                <img src="/img/ico_down_fff.svg" alt="" />
              </span>
            </BS.Btn1Type5>
            <BS.Btn1Type1
              type="button"
              onClick={() => setUserAddModalState(true)}
            >
              등록
            </BS.Btn1Type1>
            <BS.Btn1Type5 type="button" onClick={handleSelectApproval}>
              선택 승인
            </BS.Btn1Type5>
            <BS.Btn1Type2 type="button" onClick={handleSelectCompanion}>
              선택 반려
            </BS.Btn1Type2>
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>
                  <IS.CheckBoxType1>
                    <input
                      type="checkbox"
                      id="all"
                      onChange={(e) => handleAllCheck(e.target.checked)}
                      ref={allCheckBtnRef}
                      checked={
                        checkItems.length === userListState?.length &&
                        userListState?.length !== 0
                          ? true
                          : false
                      }
                    />
                    <label htmlFor="all" className="img-box">
                      <img src="/img/ico_check_1.svg" alt="" />
                    </label>
                  </IS.CheckBoxType1>
                </th>
                <th>No</th>
                <th>아이디</th>
                <th>회원 구분</th>
                <th>사이트</th>
                <th>지역 구분</th>
                <th>이름</th>
                <th>휴대전화</th>
                <th>이메일</th>
                <th>등록일</th>
                <th>승인상태</th>
              </tr>
            </thead>
            <tbody>
              {userListState?.map((list, index) => (
                <tr key={index}>
                  <td>
                    <IS.CheckBoxType1>
                      <input
                        type="checkbox"
                        id={`cv-${index}`}
                        onChange={(e) =>
                          handleSingleCheck(e.target.checked, Number(list.id))
                        }
                        checked={
                          checkItems.includes(Number(list.id)) ? true : false
                        }
                      />
                      <label htmlFor={`cv-${index}`} className="img-box">
                        <img src="/img/ico_check_1.svg" alt="" />
                      </label>
                    </IS.CheckBoxType1>
                  </td>
                  <td>{(offset + index + 1).toLocaleString()}</td>
                  <td
                    className="button-text"
                    onClick={() => {
                      setModifyNum(list.id);
                      setUserModifyModalState(true);
                    }}
                  >
                    {list.passid}
                  </td>
                  <td>{list.userType}</td>
                  <td>{list.site}</td>
                  <td>{list.regionClassification}</td>
                  <td>{list.name}</td>
                  <td>{list.phone}</td>
                  <td>{list.email}</td>
                  <td>{list.createdAt}</td>
                  <td>{list.state}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {listLength !== 0 && (
          <Pagenation
            listAllLength={listLength}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </CntBox1>
      {userAddModalState && <UserAddModal />}
      {userModifyModalState && <UserModifyModal id={modifyNum} />}
    </>
  );
};

export default Userlist;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const ReportBox = styled.div`
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;

export const CheckWrap = styled(CS.Gap24Box)`
  margin-left: 16px;
`;
